/**
 * iter971k-O - Business Tax Profile Wizard (redesigned)
 *
 * 6 essential questions - each maps to at least one downstream tax flag.
 * Users can also click "Skip for now"; we then remind them where to find
 * the wizard again (Settings → Tax → "Set up my business tax profile").
 *
 * Downstream: writes `sector`, `size_band`, `has_employees`,
 * `registration_location`, `vat_position`, `wht_agent`, `wht_credit_expected`
 * to `POST /api/business/tax-profile`. Legacy fields are kept for
 * backward-compat via the backend's `_backfill_new_from_legacy`.
 */
import React, { useMemo, useState, useEffect } from 'react';
import { usStateLabel } from '../../lib/payrollOptions';
import axios from 'axios';
import { API } from '../../App';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Checkbox } from '../ui/checkbox';
import { Alert, AlertDescription } from '../ui/alert';
import { toast } from 'sonner';
import { Loader2, Sparkles, ArrowLeft, ArrowRight, CheckCircle2, Settings } from 'lucide-react';

// ─────────────────────────────────────────────────────────
// Question set - 6 questions, each with clear tax-engine impact.
// ─────────────────────────────────────────────────────────

const SECTOR_OPTIONS = [
  { value: 'trading',                    label: 'Trading / Retail' },
  { value: 'services_general',           label: 'Services (general)' },
  { value: 'professional_practice',      label: 'Professional practice',
    hint: 'accounting, law, consulting, medicine, engineering' },
  { value: 'manufacturing',              label: 'Manufacturing / Production' },
  { value: 'agriculture',                label: 'Agriculture, Fishing, Livestock' },
  { value: 'real_estate',                label: 'Real estate / Property' },
  { value: 'financial_services',         label: 'Financial services',
    hint: 'bank, insurance, fintech, MFB' },
  { value: 'education_health_nonprofit', label: 'Education / Healthcare / Non-profit' },
  { value: 'digital_services',           label: 'Digital / Tech services',
    hint: 'SaaS, software, streaming' },
  { value: 'other',                      label: 'Something else' },
];

// Country-scoped size copy (bands baked into the label so users don't have
// to memorise thresholds). The backend size_band values stay universal.
const SIZE_OPTIONS = {
  NG: [
    { value: 'small',    label: 'Small', hint: 'Under ₦100M turnover AND under ₦250M assets' },
    { value: 'medium',   label: 'Medium', hint: '₦100M to ₦1B turnover' },
    { value: 'large',    label: 'Large', hint: 'Above ₦1B turnover' },
    { value: 'not_sure', label: 'I\u2019m not sure yet' },
  ],
  GB: [
    { value: 'small',    label: 'Small', hint: 'Under £2M turnover AND under £5M assets' },
    { value: 'medium',   label: 'Medium', hint: '£2M to £36M turnover' },
    { value: 'large',    label: 'Large', hint: 'Above £36M turnover' },
    { value: 'not_sure', label: 'I\u2019m not sure yet' },
  ],
  DEFAULT: [
    { value: 'small',    label: 'Small business' },
    { value: 'medium',   label: 'Medium business' },
    { value: 'large',    label: 'Large business' },
    { value: 'not_sure', label: 'I\u2019m not sure yet' },
  ],
};

const EMPLOYEE_OPTIONS = [
  { value: 'none',      label: 'No. I\u2019m the only worker',
    hint: 'We\u2019ll skip PAYE, NHF, NSITF and ITF suggestions.' },
  { value: 'under_5',   label: 'Yes. Fewer than 5 people' },
  { value: '5_to_20',   label: 'Yes. Between 5 and 20 people' },
  { value: 'over_20',   label: 'Yes. More than 20 people' },
];

const REGISTRATION_OPTIONS = {
  NG: [
    { value: 'local',            label: 'Registered in Nigeria' },
    { value: 'non_resident',     label: 'Registered outside Nigeria',
      hint: 'We\u2019ll apply non-resident tax rules (4% flat CIT).' },
    { value: 'free_trade_zone',  label: 'Free Trade / Export Processing Zone',
      hint: 'FTZ enterprises receive VAT / WHT relief.' },
  ],
  DEFAULT: [
    { value: 'local',            label: 'Registered in this country' },
    { value: 'non_resident',     label: 'Registered outside. With operations here' },
    { value: 'free_trade_zone',  label: 'Free trade / Special economic zone' },
  ],
};

const VAT_OPTIONS = {
  NG: [
    { value: 'registered',                      label: 'Yes. We\u2019re registered and charge VAT',
      hint: 'We\u2019ll auto-apply 7.5% VAT on qualifying sales and claim input VAT on bills.' },
    { value: 'not_registered_below_threshold',  label: 'No. Our turnover is below the VAT threshold',
      hint: 'We won\u2019t add VAT to your invoices but will still track any input VAT you incur.' },
    { value: 'exempt_sector',                   label: 'Our sales are VAT-exempt',
      hint: 'e.g. basic food, medical, education, exports.' },
    { value: 'not_sure',                        label: 'I\u2019m not sure' },
  ],
  DEFAULT: [
    { value: 'registered',                      label: 'Registered. We charge sales tax / VAT' },
    { value: 'not_registered_below_threshold',  label: 'Not registered. We\u2019re below the threshold' },
    { value: 'exempt_sector',                   label: 'Our sales are exempt from VAT' },
    { value: 'not_sure',                        label: 'I\u2019m not sure' },
  ],
};

// ─────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────

export function BusinessTaxProfileWizard({ open, onClose, onCompleted, country }) {
  const cc = (country || '').toUpperCase();
  const sizeOptions = SIZE_OPTIONS[cc] || SIZE_OPTIONS.DEFAULT;
  const regOptions = REGISTRATION_OPTIONS[cc] || REGISTRATION_OPTIONS.DEFAULT;
  const vatOptions = VAT_OPTIONS[cc] || VAT_OPTIONS.DEFAULT;

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  const [supportedStates, setSupportedStates] = useState([]);
  const [profile, setProfile] = useState({
    sector: null,
    size_band: null,
    has_employees: null,
    registration_location: null,
    vat_position: null,
    wht_agent: null,
    wht_credit_expected: null,
    wht_none: false, // convenience flag for "neither applies"
    // iter1094-us-sales-tax - US-only fields.
    tax_state: null,
    us_sales_categories: [],
    sales_tax_registered: null,
  });

  const isUS = cc === 'US';

  useEffect(() => {
    if (!open) return;
    setStep(0);
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/business/tax-profile`);
        const p = res.data?.profile || {};
        setProfile((prev) => ({
          ...prev, ...p,
          us_sales_categories: p.us_sales_categories || [],
          wht_none: p.wht_agent === false && p.wht_credit_expected === false,
        }));
      } catch { /* first-time */ }
      finally { setLoading(false); }
    })();
    // iter1094-us-sales-tax - Pre-load the supported US states so the
    // dropdown can render the moment the US branch step is opened.
    if (isUS) {
      axios.get(`${API}/tax/us/states`)
        .then((res) => setSupportedStates(res.data?.supported || []))
        .catch(() => setSupportedStates([]));
    }
  }, [open, isUS]);

  const set = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  const toggleWht = (which) => {
    setProfile((p) => {
      if (which === 'none') {
        return { ...p, wht_none: !p.wht_none, wht_agent: false, wht_credit_expected: false };
      }
      const next = { ...p, wht_none: false, [which]: !p[which] };
      return next;
    });
  };

  const steps = [
    {
      key: 'sector',
      title: 'What best describes your main business activity?',
      subtitle: 'This helps us know which tax rules typically apply.',
      canContinue: !!profile.sector,
      render: (
        <RadioGroup
          value={profile.sector || ''}
          onValueChange={(v) => set('sector', v)}
          className="space-y-2"
        >
          {SECTOR_OPTIONS.map((o) => (
            <label key={o.value}
              className="flex items-start gap-3 p-3 rounded-lg border hover:border-blue-500 cursor-pointer"
              data-testid={`tp-sector-${o.value}`}>
              <RadioGroupItem value={o.value} className="mt-0.5" />
              <div className="flex-1">
                <span className="text-sm font-medium">{o.label}</span>
                {o.hint && <p className="text-xs text-gray-500 mt-0.5">{o.hint}</p>}
              </div>
            </label>
          ))}
        </RadioGroup>
      ),
    },
    {
      key: 'size',
      title: 'How would you size your business?',
      subtitle: 'This drives small-business exemptions and the CIT rate we suggest.',
      canContinue: !!profile.size_band,
      render: (
        <RadioGroup
          value={profile.size_band || ''}
          onValueChange={(v) => set('size_band', v)}
          className="space-y-2"
        >
          {sizeOptions.map((o) => (
            <label key={o.value}
              className="flex items-start gap-3 p-3 rounded-lg border hover:border-blue-500 cursor-pointer"
              data-testid={`tp-size-${o.value}`}>
              <RadioGroupItem value={o.value} className="mt-0.5" />
              <div className="flex-1">
                <span className="text-sm font-medium">{o.label}</span>
                {o.hint && <p className="text-xs text-gray-500 mt-0.5">{o.hint}</p>}
              </div>
            </label>
          ))}
        </RadioGroup>
      ),
    },
    {
      key: 'employees',
      title: 'Do you have staff on your payroll?',
      subtitle: 'This tells us which payroll taxes to suggest (PAYE, NHF, NSITF, ITF).',
      canContinue: !!profile.has_employees,
      render: (
        <RadioGroup
          value={profile.has_employees || ''}
          onValueChange={(v) => set('has_employees', v)}
          className="space-y-2"
        >
          {EMPLOYEE_OPTIONS.map((o) => (
            <label key={o.value}
              className="flex items-start gap-3 p-3 rounded-lg border hover:border-blue-500 cursor-pointer"
              data-testid={`tp-employees-${o.value}`}>
              <RadioGroupItem value={o.value} className="mt-0.5" />
              <div className="flex-1">
                <span className="text-sm font-medium">{o.label}</span>
                {o.hint && <p className="text-xs text-gray-500 mt-0.5">{o.hint}</p>}
              </div>
            </label>
          ))}
        </RadioGroup>
      ),
    },
    {
      key: 'registration',
      title: 'Where is your business officially registered?',
      subtitle: 'Non-resident and free-trade-zone businesses have different tax rules.',
      canContinue: !!profile.registration_location,
      render: (
        <RadioGroup
          value={profile.registration_location || ''}
          onValueChange={(v) => set('registration_location', v)}
          className="space-y-2"
        >
          {regOptions.map((o) => (
            <label key={o.value}
              className="flex items-start gap-3 p-3 rounded-lg border hover:border-blue-500 cursor-pointer"
              data-testid={`tp-registration-${o.value}`}>
              <RadioGroupItem value={o.value} className="mt-0.5" />
              <div className="flex-1">
                <span className="text-sm font-medium">{o.label}</span>
                {o.hint && <p className="text-xs text-gray-500 mt-0.5">{o.hint}</p>}
              </div>
            </label>
          ))}
        </RadioGroup>
      ),
    },
    {
      key: 'vat',
      title: 'What\u2019s your VAT position?',
      subtitle: 'Tells us whether to add VAT to invoices and claim input VAT on bills.',
      canContinue: !!profile.vat_position,
      render: (
        <RadioGroup
          value={profile.vat_position || ''}
          onValueChange={(v) => set('vat_position', v)}
          className="space-y-2"
        >
          {vatOptions.map((o) => (
            <label key={o.value}
              className="flex items-start gap-3 p-3 rounded-lg border hover:border-blue-500 cursor-pointer"
              data-testid={`tp-vat-${o.value}`}>
              <RadioGroupItem value={o.value} className="mt-0.5" />
              <div className="flex-1">
                <span className="text-sm font-medium">{o.label}</span>
                {o.hint && <p className="text-xs text-gray-500 mt-0.5">{o.hint}</p>}
              </div>
            </label>
          ))}
        </RadioGroup>
      ),
    },
    {
      key: 'wht',
      title: 'How does withholding tax work in your business?',
      subtitle: 'Pick every option that applies to you (or the last one if none do).',
      canContinue: (
        !!profile.wht_agent || !!profile.wht_credit_expected || !!profile.wht_none
      ),
      render: (
        <div className="space-y-2">
          {[
            { key: 'wht_agent',
              title: 'We deduct WHT from vendors we pay',
              hint: 'e.g. rent, professional services, contractors: we\u2019ll auto-suggest WHT on your bills.' },
            { key: 'wht_credit_expected',
              title: 'Our customers withhold WHT from what they pay us',
              hint: 'We\u2019ll track your WHT credits so you can claim them at year-end.' },
            { key: 'none',
              title: 'Neither / Not sure',
              hint: 'You can change this any time from Settings.' },
          ].map((o) => {
            const checked =
              o.key === 'none' ? !!profile.wht_none : !!profile[o.key];
            return (
              <label key={o.key}
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${checked ? 'border-blue-500 bg-blue-50' : ''}`}
                data-testid={`tp-wht-${o.key}`}>
                <Checkbox checked={checked}
                  onCheckedChange={() => toggleWht(o.key)}
                  className="mt-0.5" />
                <div className="flex-1">
                  <span className="text-sm font-medium">{o.title}</span>
                  <p className="text-xs text-gray-500 mt-0.5">{o.hint}</p>
                </div>
              </label>
            );
          })}
        </div>
      ),
    },
  ];

  // iter1094-us-sales-tax - Insert 3 US-only steps between the base
  // wizard and its footer. Only inject when the business country is US.
  const US_SALES_CATEGORY_OPTIONS = [
    { value: 'physical_goods', label: 'Physical goods (retail, wholesale)' },
    { value: 'services',       label: 'Services (professional, personal)' },
    { value: 'digital_saas',   label: 'Digital goods / SaaS' },
    { value: 'food_beverage',  label: 'Food & beverage' },
    { value: 'medical',        label: 'Medical / healthcare' },
    { value: 'mixed',          label: 'Mixed - several of the above' },
    { value: 'not_sure',       label: 'I\u2019m not sure yet' },
  ];
  const US_REGISTERED_OPTIONS = [
    { value: 'registered',                     label: 'Yes - we\u2019re registered and collect sales tax' },
    { value: 'not_registered_below_threshold', label: 'Not yet - we\u2019re under the state threshold' },
    { value: 'not_sure',                       label: 'I\u2019m not sure' },
  ];
  const toggleCategory = (v) => setProfile((p) => {
    const arr = p.us_sales_categories || [];
    return { ...p, us_sales_categories: arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v] };
  });
  if (isUS) {
    steps.push(
      {
        key: 'us_state',
        title: 'Which US state is your business based in?',
        subtitle: 'We use this to apply the right state sales-tax rate on every invoice, bill and bank feed line.',
        canContinue: !!profile.tax_state,
        render: (
          <div className="space-y-2" data-testid="tp-us-state-picker">
            {supportedStates.length === 0 ? (
              <p className="text-xs text-gray-500 py-4">Loading states...</p>
            ) : (
              <select
                className="w-full border rounded-lg p-2 text-sm bg-white"
                value={profile.tax_state || ''}
                onChange={(e) => set('tax_state', e.target.value.toUpperCase() || null)}
                data-testid="tp-us-state-select"
              >
                <option value="">-- Select your state --</option>
                {supportedStates.map((s) => (
                  <option key={s} value={s}>{usStateLabel(s)}</option>
                ))}
                <option disabled>-- Non-shipped states below --</option>
                {[
                  'AL','AK','AR','CO','CT','DE','HI','ID','IN','IA','KS','KY','LA','ME','MD','MN','MS','MO','MT','NE','NV','NH','NM','ND','OK','OR','RI','SC','SD','TN','UT','VT','WV','WI','WY','DC',
                ].filter((s) => !supportedStates.includes(s)).sort().map((s) => (
                  <option key={s} value={s}>{usStateLabel(s)}</option>
                ))}
              </select>
            )}
            <p className="text-[11px] text-gray-500">
              We ship full category-taxability data for {supportedStates.length} states. Other states will use
              national defaults - confirm with your state DOR.
            </p>
          </div>
        ),
      },
      {
        key: 'us_categories',
        title: 'What does your business sell?',
        subtitle: 'Sales-tax rules differ per category. Pick every one that applies.',
        canContinue: (profile.us_sales_categories || []).length > 0,
        render: (
          <div className="space-y-2">
            {US_SALES_CATEGORY_OPTIONS.map((o) => {
              const checked = (profile.us_sales_categories || []).includes(o.value);
              return (
                <label key={o.value}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${checked ? 'border-blue-500 bg-blue-50' : ''}`}
                  data-testid={`tp-us-category-${o.value}`}>
                  <Checkbox checked={checked} onCheckedChange={() => toggleCategory(o.value)} className="mt-0.5" />
                  <span className="text-sm font-medium">{o.label}</span>
                </label>
              );
            })}
          </div>
        ),
      },
      {
        key: 'us_registered',
        title: 'Are you registered to collect sales tax?',
        subtitle: 'Tells us whether to auto-suggest sales tax on your invoices.',
        canContinue: !!profile.sales_tax_registered,
        render: (
          <RadioGroup
            value={profile.sales_tax_registered || ''}
            onValueChange={(v) => set('sales_tax_registered', v)}
            className="space-y-2"
          >
            {US_REGISTERED_OPTIONS.map((o) => (
              <label key={o.value}
                className="flex items-start gap-3 p-3 rounded-lg border hover:border-blue-500 cursor-pointer"
                data-testid={`tp-us-registered-${o.value}`}>
                <RadioGroupItem value={o.value} className="mt-0.5" />
                <span className="text-sm font-medium">{o.label}</span>
              </label>
            ))}
          </RadioGroup>
        ),
      },
    );
  }

  const total = steps.length;
  const current = steps[step];

  const buildPayload = () => {
    const p = { ...profile };
    delete p.wht_none;
    // Explicit false is more useful than null for the "neither" answer.
    if (profile.wht_none) {
      p.wht_agent = false;
      p.wht_credit_expected = false;
    }
    // Reset deferred: any save = user is engaged again.
    p.deferred = false;
    return p;
  };

  const handleNext = () => {
    if (step < total - 1) setStep(step + 1);
    else handleSave();
  };
  const handleBack = () => step > 0 && setStep(step - 1);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/business/tax-profile`, buildPayload());
      toast.success('Tax profile saved: we\u2019ll use this to guide every tax suggestion.');
      if (onCompleted) await onCompleted();
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save tax profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSkipConfirm = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/business/tax-profile/defer`);
      toast.info('We\u2019ll ask again later. Settings → Tax → "Set up my business tax profile" whenever you\u2019re ready.');
      if (onCompleted) await onCompleted();
      setShowSkipConfirm(false);
      onClose && onClose();
    } catch {
      toast.error('Could not save your preference. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
        <DialogContent
          className="max-w-lg w-[calc(100vw-2rem)] sm:w-full max-h-[92dvh] p-0 gap-0 flex flex-col overflow-hidden rounded-xl"
          data-testid="business-tax-profile-wizard"
        >
          <DialogHeader className="shrink-0 px-4 pt-4 pb-1 sm:px-6 sm:pt-5 text-left">
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-blue-600" />
              Tell us about your business
            </DialogTitle>
            <DialogDescription className="text-xs">
              Six quick questions so we can suggest the right taxes on every invoice,
              bill and bank line: no jargon, no guesswork.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-blue-600" />
            </div>
          ) : (
            <>
              <div className="shrink-0 flex items-center gap-1 px-4 sm:px-6 pt-3 pb-2" data-testid="tp-progress">
                {steps.map((_, i) => (
                  <div key={i}
                    className={`h-1.5 flex-1 rounded-full ${i <= step ? 'bg-blue-600' : 'bg-slate-200'}`} />
                ))}
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 pb-3 overscroll-contain">
                <h3 className="font-semibold text-base mb-1">{current.title}</h3>
                <p className="text-xs text-gray-500 mb-3">{current.subtitle}</p>
                {current.render}

                <Alert className="bg-slate-50 border-slate-200 mt-3">
                  <AlertDescription className="text-[11px] text-slate-600">
                    Your answers stay private inside your workspace. We only use them to
                    suggest correct taxes on your invoices, bills and bank feed. Change
                    them any time from Settings → Tax.
                  </AlertDescription>
                </Alert>
              </div>

              <DialogFooter className="shrink-0 border-t border-slate-100 bg-white px-4 sm:px-6 py-3 flex-row items-center justify-between gap-2 sm:justify-between">
                <div className="flex items-center gap-1.5">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleBack}
                    disabled={step === 0 || saving}
                    data-testid="tp-back-btn"
                  >
                    <ArrowLeft className="w-4 h-4 sm:mr-1" />
                    <span className="hidden sm:inline">Back</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs text-slate-500 hover:text-slate-800 px-2"
                    onClick={() => setShowSkipConfirm(true)}
                    disabled={saving}
                    data-testid="tp-skip-btn"
                  >
                    Skip for now
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-gray-400 whitespace-nowrap">{step + 1} / {total}</span>
                  <Button
                    size="sm"
                    onClick={handleNext}
                    disabled={!current.canContinue || saving}
                    data-testid={step === total - 1 ? 'tp-finish-btn' : 'tp-next-btn'}
                  >
                    {saving ? (
                      <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving\u2026</>
                    ) : step === total - 1 ? (
                      <><CheckCircle2 className="w-4 h-4 mr-1" /> Finish</>
                    ) : (
                      <>Next <ArrowRight className="w-4 h-4 ml-1" /></>
                    )}
                  </Button>
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Skip-for-now confirmation with a clear "where to find it" hint. */}
      <Dialog open={showSkipConfirm} onOpenChange={setShowSkipConfirm}>
        <DialogContent
          className="max-w-md w-[calc(100vw-2rem)] sm:w-full max-h-[85dvh] overflow-y-auto rounded-xl"
          data-testid="tp-skip-confirm-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-slate-600" />
              Skip for now?
            </DialogTitle>
            <DialogDescription className="pt-2 space-y-3 text-sm text-slate-700">
              No problem: you can finish this any time.
              <br /><br />
              Until it&rsquo;s done, we&rsquo;ll use safe defaults (no auto VAT, no auto
              WHT) so nothing wrong ever posts to your books.
              <br /><br />
              <span className="block bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs">
                <strong className="text-slate-800">How to find this later:</strong><br />
                Go to <span className="font-medium">Settings → Tax</span> and tap
                <span className="font-medium"> &ldquo;Set up my business tax profile&rdquo;</span>.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowSkipConfirm(false)}
              disabled={saving}
              data-testid="tp-skip-cancel-btn"
            >
              Keep going
            </Button>
            <Button
              onClick={handleSkipConfirm}
              disabled={saving}
              data-testid="tp-skip-confirm-btn"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</>
              ) : (
                <>Skip and remind me</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default BusinessTaxProfileWizard;
